import { useEffect, useRef, useState } from "react";

const API_SCRIPT = "https://www.youtube.com/iframe_api";

interface YTPlayerOptions {
  videoId?: string;
  height?: string | number;
  width?: string | number;
  playerVars?: Record<string, string | number>;
  events?: {
    onReady?: (event: { target: YTPlayer }) => void;
    onStateChange?: (event: { data: number; target: YTPlayer }) => void;
  };
}

interface YTPlayer {
  playVideo(): void;
  pauseVideo(): void;
  getPlayerState(): number;
  destroy(): void;
}

interface YTNamespace {
  Player: new (element: string | HTMLElement, options: YTPlayerOptions) => YTPlayer;
  PlayerState: Record<"ENDED" | "PLAYING" | "PAUSED" | "BUFFERING" | "CUED", number>;
}

declare global {
  interface Window {
    YT?: YTNamespace;
    onYouTubeIframeAPIReady?: () => void;
  }
}

export const PLAYER_STATE = {
  UNSTARTED: -1,
  ENDED: 0,
  PLAYING: 1,
  PAUSED: 2,
  BUFFERING: 3,
  CUED: 5,
} as const;

let ytLoadPromise: Promise<YTNamespace> | null = null;

function loadYTApi(): Promise<YTNamespace> {
  if (ytLoadPromise) return ytLoadPromise;

  ytLoadPromise = new Promise<YTNamespace>((resolve, reject) => {
    if (window.YT) {
      resolve(window.YT);
      return;
    }
    window.onYouTubeIframeAPIReady = () => {
      if (window.YT) resolve(window.YT);
      else reject(new Error("YouTube iframe API did not initialize."));
    };
    const script = document.createElement("script");
    script.src = API_SCRIPT;
    script.async = true;
    script.onerror = () => reject(new Error("Could not load the YouTube player."));
    document.head.appendChild(script);
  });

  return ytLoadPromise;
}

export interface YouTubePlayerHook {
  containerRef: React.RefObject<HTMLDivElement | null>;
  ready: boolean;
  state: number;
  error: string | null;
  play: () => void;
}

export function useYouTubePlayer(videoId: string, onEnded?: () => void): YouTubePlayerHook {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [ready, setReady] = useState(false);
  const [state, setState] = useState<number>(PLAYER_STATE.UNSTARTED);
  const [error, setError] = useState<string | null>(null);
  const playerRef = useRef<YTPlayer | null>(null);
  const onEndedRef = useRef(onEnded);
  onEndedRef.current = onEnded;

  useEffect(() => {
    let cancelled = false;
    let created: YTPlayer | null = null;

    loadYTApi()
      .then((YT) => {
        if (cancelled || !containerRef.current) return;
        created = new YT.Player(containerRef.current, {
          videoId,
          playerVars: { playsinline: 1, rel: 0 },
          events: {
            onReady: () => {
              if (cancelled) return;
              setReady(true);
            },
            onStateChange: (event) => {
              if (cancelled) return;
              setState(event.data);
              if (event.data === PLAYER_STATE.ENDED) onEndedRef.current?.();
            },
          },
        });
        playerRef.current = created;
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(e instanceof Error ? e.message : "Failed to load the player.");
      });

    return () => {
      cancelled = true;
      created?.destroy();
      playerRef.current = null;
      setReady(false);
      setState(PLAYER_STATE.UNSTARTED);
    };
  }, [videoId]);

  return {
    containerRef,
    ready,
    state,
    error,
    play: () => playerRef.current?.playVideo(),
  };
}